import { FieldStateRef } from '@/classes/FieldReference';
import { RuleFieldMessage } from './rules/RuleFieldMessage';
import { UsageStatus } from '@/classes/rules/UsageStatus';

export type ConstructorOptions = {
  key: string
  path: string[]
  state: FieldStateRef
  msg: string
  value?: unknown
  ruleMessages?: RuleFieldMessage[]
  preventReport?: UsageStatus
}

/**
 * "Reporting" class to synthetize a field current state
 */
export class FieldStatus {

  /**
   * The field key
   */
  public key: string;
  /**
   * The field path in its context
   */
  public path: string[];
  /**
   * The field status
   */
  public state: FieldStateRef;
  /**
   * A message explaining the field status
   */
  public msg: string;
  /**
   * The field value
   */
  public value: any;

  public ruleMessages: RuleFieldMessage[];

  private readonly preventReport: UsageStatus;

  constructor(options: ConstructorOptions) {
    this.key = options.key;
    this.path = options.path;
    this.state = options.state;
    this.msg = options.msg;
    this.value = options.value;
    this.ruleMessages = options.ruleMessages ?? [];
    this.preventReport = options.preventReport ?? new UsageStatus({ preSizing: false });
  }

  /**
   * The field is valid if it is set and not an error (so an unavailable field or a no rule field is considered valid)
   */
  public isValid(): boolean {
    return this.state !== FieldStateRef.not_set && this.state !== FieldStateRef.error;
  }

  public isUsedForPreSizing(): boolean {
    return this.preventReport.isUsedForPreSizing();
  }

  public isUsedForPvCurve(): boolean {
    return this.preventReport.isUsedForPvCurve();
  }

  public addSubFieldStatuses(fieldStatuses: FieldStatus[]): FieldStatus {
    return fieldStatuses
      .filter(status => !status.isValid())
      .reduce((accumulator, error) => accumulator.addErrorInSubfields(error), this.copy());
  }

  private addErrorInSubfields(fieldStatus: FieldStatus): FieldStatus {
    return this.copy({
      state: FieldStateRef.error,
      msg: 'Field has errors in its subfields',
      preventReport: this.preventReport.combine(fieldStatus.preventReport)
    });
  }

  private copy(changes: Partial<ConstructorOptions> = {}): FieldStatus {
    return new FieldStatus({
      key: changes.key ?? this.key,
      path: changes.path ?? this.path,
      state: changes.state ?? this.state,
      msg: changes.msg ?? this.msg,
      preventReport: changes.preventReport ?? this.preventReport,
      value: changes.value ?? this.value,
      ruleMessages: changes.ruleMessages ?? this.ruleMessages
    });
  }
}
